import type * as React from "react";

/** Persistent inline alert (contrast with the transient Toast). */
export interface BannerProps {
  /** @default "info" */
  status?: BannerStatus;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Optional action node (e.g. a Button) shown below the message. */
  action?: React.ReactNode;
  onClose?: () => void;
  /** Override the default status icon. */
  icon?: React.ReactNode;
  style?: React.CSSProperties;
}

export type BannerStatus = "info" | "success" | "warning" | "error" | "neutral";

const TONE: Record<BannerStatus, string> = {
  info: "var(--accent)",
  success: "var(--green-500)",
  warning: "var(--gold-500)",
  error: "var(--red-500)",
  neutral: "var(--text-secondary)",
};

const ICON: Record<BannerStatus, React.ReactNode> = {
  info: <path d="M12 16v-4M12 8h.01" />,
  success: <polyline points="20 6 9 17 4 12" />,
  warning: <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />,
  error: <path d="M18 6 6 18M6 6l12 12" />,
  neutral: <path d="M12 16v-4M12 8h.01" />,
};

const XIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * Salesko Banner — persistent inline alert (vs the transient Toast). Full-width tinted
 * surface with a status icon, optional title, action and dismiss.
 */
export function Banner({ status = "info", title, children, action, onClose, icon, style }: BannerProps) {
  const color = TONE[status];

  return (
    <div
      role="status"
      style={
        {
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "13px 14px",
          borderRadius: "var(--radius-md)",
          background: `color-mix(in srgb, ${color} 9%, var(--surface-card))`,
          border: `1px solid color-mix(in srgb, ${color} 28%, transparent)`,
          fontFamily: "var(--font-sans)",
          ...style,
        } as React.CSSProperties
      }
    >
      <span style={{ color, display: "inline-flex", flex: "none", marginTop: 1 }}>
        {icon || (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {ICON[status]}
          </svg>
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontSize: "var(--text-body-md)", fontWeight: "var(--fw-bold)", color: "var(--text-strong)" }}>
            {title}
          </div>
        )}
        {children && (
          <div
            style={{
              fontSize: "var(--text-body-sm)",
              color: "var(--text-body)",
              marginTop: title ? 2 : 0,
              lineHeight: 1.5,
            }}
          >
            {children}
          </div>
        )}
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Dismiss"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: 2,
            marginTop: -1,
            lineHeight: 0,
            flex: "none",
          }}
        >
          <XIcon />
        </button>
      )}
    </div>
  );
}
